/**
 * QR code tracking — operator UI (feature #170).
 *
 * Composition:
 *   - Header with emerald->sky gradient + subtitle.
 *   - Affiliate filter (text input + apply button).
 *   - Create form: targetUrl + label + optional short-url origin.
 *   - Summary row: total / active / disabled / total scans / last scan.
 *   - Table: label, slug, short URL preview, target URL, scans, last scan,
 *     status badge, SVG preview link, record-scan + delete actions.
 *   - Empty + loading states, toast feedback via Sonner.
 *
 * Data layer: TanStack Query for fetch + mutations. SVG is fetched lazily
 * through an `<img>` tag pointed at `buildQrSvgUrl(slug)` — no binary in
 * state, no bitmaps persisted server side.
 *
 * Brand: emerald->sky headline gradient, zinc borders, 6-8px rounded, light
 * theme only, no emojis, no competitor names.
 */

import { useCallback, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	buildQrSvgUrl,
	buildShortUrlPreview,
	computeQrSummary,
	createQrCode,
	deleteQrCode,
	fetchQrCodesForAffiliate,
	formatInteger,
	formatScanTimestamp,
	formatStatusLabel,
	isValidAffiliateId,
	isValidLabel,
	isValidTargetUrl,
	qrTrackingQueryKeys,
	recordQrScan,
	sortQrRows,
	type QrTrackingRow,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — mirror affiliate-manager; zinc borders, 6-8px rounded.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-500 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE_ACTIVE =
	"inline-flex items-center rounded-[4px] border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700";
const BADGE_DISABLED =
	"inline-flex items-center rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-xs font-medium text-zinc-600";
const HEADLINE =
	"bg-gradient-to-r from-emerald-600 to-sky-500 bg-clip-text text-transparent";

// ---------------------------------------------------------------------------
// Props + defaults
// ---------------------------------------------------------------------------

const DEFAULT_AFFILIATE_ID = "aff_demo";
const DEFAULT_ORIGIN_PLACEHOLDER = "https://trk.example";

export interface QrTrackingPageProps {
	/**
	 * Optional affiliate id to seed the filter with. When omitted or invalid
	 * we fall back to a known demo value so the page renders something on
	 * first paint without forcing the operator to type.
	 */
	initialAffiliateId?: string;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function QrTrackingPage(props: QrTrackingPageProps = {}) {
	const queryClient = useQueryClient();

	const initialAffiliate =
		props.initialAffiliateId && isValidAffiliateId(props.initialAffiliateId)
			? props.initialAffiliateId
			: DEFAULT_AFFILIATE_ID;

	const [affiliateId, setAffiliateId] = useState<string>(initialAffiliate);
	const [pendingAffiliate, setPendingAffiliate] = useState(initialAffiliate);

	// Create form state.
	const [targetUrl, setTargetUrl] = useState("");
	const [label, setLabel] = useState("");
	const [originOverride, setOriginOverride] = useState("");

	// Preview toggle: which slug is the operator expanding the SVG for.
	const [previewSlug, setPreviewSlug] = useState<string | null>(null);

	// -------------------------------------------------------------------
	// Query: list QR codes for the currently-selected affiliate.
	// -------------------------------------------------------------------
	const listQuery = useQuery({
		queryKey: qrTrackingQueryKeys.byAffiliate(affiliateId),
		queryFn: () => fetchQrCodesForAffiliate(affiliateId),
		enabled: isValidAffiliateId(affiliateId),
	});

	const rows = useMemo<QrTrackingRow[]>(
		() => sortQrRows(listQuery.data?.codes ?? []),
		[listQuery.data?.codes],
	);
	const summary = useMemo(() => computeQrSummary(rows), [rows]);

	// -------------------------------------------------------------------
	// Mutations.
	// -------------------------------------------------------------------
	const createMutation = useMutation({
		mutationFn: (args: { targetUrl: string; label: string; origin?: string }) =>
			createQrCode({
				affiliateId,
				targetUrl: args.targetUrl,
				label: args.label,
				origin: args.origin,
			}),
		onSuccess: () => {
			toast.success("QR code minted.");
			setTargetUrl("");
			setLabel("");
			queryClient.invalidateQueries({
				queryKey: qrTrackingQueryKeys.byAffiliate(affiliateId),
			});
		},
		onError: (err: Error) => toast.error(err.message || "Unable to mint QR."),
	});

	const scanMutation = useMutation({
		mutationFn: (slug: string) => recordQrScan(slug),
		onSuccess: (_res, slug) => {
			toast.success(`Scan recorded for ${slug}.`);
			queryClient.invalidateQueries({
				queryKey: qrTrackingQueryKeys.byAffiliate(affiliateId),
			});
		},
		onError: (err: Error) => toast.error(err.message || "Unable to record scan."),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteQrCode(id),
		onSuccess: (_res, id) => {
			toast.success("QR code removed.");
			if (previewSlug) setPreviewSlug(null);
			queryClient.invalidateQueries({
				queryKey: qrTrackingQueryKeys.byAffiliate(affiliateId),
			});
			void id;
		},
		onError: (err: Error) => toast.error(err.message || "Unable to remove QR."),
	});

	const busy =
		createMutation.isPending ||
		scanMutation.isPending ||
		deleteMutation.isPending;

	// -------------------------------------------------------------------
	// Handlers
	// -------------------------------------------------------------------
	const handleApplyAffiliate = useCallback(() => {
		const trimmed = pendingAffiliate.trim();
		if (!isValidAffiliateId(trimmed)) {
			toast.error("Affiliate id must be 1-128 chars of [A-Za-z0-9_.:-].");
			return;
		}
		setAffiliateId(trimmed);
		setPreviewSlug(null);
	}, [pendingAffiliate]);

	const handleCreate = useCallback(() => {
		const trimmedTarget = targetUrl.trim();
		if (!isValidTargetUrl(trimmedTarget)) {
			toast.error("Target URL must be a valid http(s) URL (<=2 KB).");
			return;
		}
		if (!isValidLabel(label)) {
			toast.error("Label must be <=120 bytes.");
			return;
		}
		const origin = originOverride.trim();
		createMutation.mutate({
			targetUrl: trimmedTarget,
			label: label.trim(),
			...(origin.length > 0 ? { origin } : {}),
		});
	}, [targetUrl, label, originOverride, createMutation]);

	const handleScan = useCallback(
		(row: QrTrackingRow) => {
			if (busy) return;
			scanMutation.mutate(row.slug);
		},
		[busy, scanMutation],
	);

	const handleDelete = useCallback(
		(row: QrTrackingRow) => {
			if (busy) return;
			deleteMutation.mutate(row.id);
		},
		[busy, deleteMutation],
	);

	const togglePreview = useCallback((slug: string) => {
		setPreviewSlug((prev) => (prev === slug ? null : slug));
	}, []);

	// -------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------
	const listError = listQuery.error as Error | undefined;

	return (
		<section className="flex flex-col gap-4">
			<header className="flex flex-col gap-1">
				<h1 className={`text-2xl font-semibold ${HEADLINE}`}>
					QR code tracking
				</h1>
				<p className="text-sm text-zinc-600">
					Mint per-affiliate QR codes and track scans. SVG is rendered on
					demand from the embedded short URL.
				</p>
			</header>

			<div className={`${CARD} flex flex-col gap-4 p-4`}>
				<div className="flex flex-col gap-2 sm:flex-row sm:items-end">
					<div className="flex flex-1 flex-col gap-1">
						<label
							htmlFor="qr-affiliate-filter"
							className={LABEL}
						>
							Affiliate id
						</label>
						<input
							id="qr-affiliate-filter"
							type="text"
							value={pendingAffiliate}
							onChange={(e) => setPendingAffiliate(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleApplyAffiliate();
							}}
							className={INPUT}
							placeholder={DEFAULT_AFFILIATE_ID}
						/>
					</div>
					<button
						type="button"
						onClick={handleApplyAffiliate}
						className={BTN_NEUTRAL}
					>
						Apply
					</button>
				</div>
				<p className="text-xs text-zinc-500">
					Showing QR codes for <span className="font-medium text-zinc-800">{affiliateId}</span>.
				</p>
			</div>

			<div className={`${CARD} flex flex-col gap-4 p-4`}>
				<h2 className="text-base font-semibold text-zinc-900">Mint a new QR</h2>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<div className="flex flex-col gap-1">
						<label htmlFor="qr-target-url" className={LABEL}>
							Target URL
						</label>
						<input
							id="qr-target-url"
							type="url"
							value={targetUrl}
							onChange={(e) => setTargetUrl(e.target.value)}
							className={INPUT}
							placeholder="https://advertiser.example/landing"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="qr-label" className={LABEL}>
							Label (optional)
						</label>
						<input
							id="qr-label"
							type="text"
							value={label}
							onChange={(e) => setLabel(e.target.value)}
							className={INPUT}
							placeholder="Spring flyer"
							maxLength={180}
						/>
					</div>
					<div className="flex flex-col gap-1 sm:col-span-2">
						<label htmlFor="qr-origin" className={LABEL}>
							Short-URL origin (optional)
						</label>
						<input
							id="qr-origin"
							type="text"
							value={originOverride}
							onChange={(e) => setOriginOverride(e.target.value)}
							className={INPUT}
							placeholder={DEFAULT_ORIGIN_PLACEHOLDER}
						/>
						<p className="text-xs text-zinc-500">
							Baked into the QR as <span className="font-mono">origin/q/&lt;slug&gt;</span>.
							Defaults to the tenant&apos;s configured short domain.
						</p>
					</div>
				</div>
				<div className="flex justify-end">
					<button
						type="button"
						onClick={handleCreate}
						disabled={busy || !targetUrl.trim()}
						className={BTN_PRIMARY}
					>
						{createMutation.isPending ? "Minting..." : "Mint QR"}
					</button>
				</div>
			</div>

			<SummaryRow summary={summary} />

			<div className={`${CARD} overflow-hidden`}>
				<div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
					<h2 className="text-base font-semibold text-zinc-900">QR codes</h2>
					<span className="text-xs text-zinc-500">
						{formatInteger(rows.length)} total
					</span>
				</div>
				{listQuery.isLoading ? (
					<div className="px-4 py-8 text-center text-sm text-zinc-500">
						Loading QR codes...
					</div>
				) : listError ? (
					<div className="px-4 py-8 text-center text-sm text-red-600">
						{listError.message || "Unable to load QR codes."}
					</div>
				) : rows.length === 0 ? (
					<div className="px-4 py-8 text-center text-sm text-zinc-500">
						No QR codes yet. Mint one above to get started.
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-zinc-200 text-sm">
							<thead className="bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
								<tr>
									<th scope="col" className="px-4 py-2">Label</th>
									<th scope="col" className="px-4 py-2">Slug</th>
									<th scope="col" className="px-4 py-2">Short URL</th>
									<th scope="col" className="px-4 py-2">Target</th>
									<th scope="col" className="px-4 py-2">Scans</th>
									<th scope="col" className="px-4 py-2">Last scan</th>
									<th scope="col" className="px-4 py-2">Status</th>
									<th scope="col" className="px-4 py-2 text-right">Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100 bg-white">
								{rows.map((row) => {
									const isOpen = previewSlug === row.slug;
									return (
										<QrRow
											key={row.id}
											row={row}
											open={isOpen}
											busy={busy}
											originOverride={originOverride}
											onToggle={() => togglePreview(row.slug)}
											onScan={() => handleScan(row)}
											onDelete={() => handleDelete(row)}
										/>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Row + preview sub-components — kept local, static styling.
// ---------------------------------------------------------------------------

interface QrRowProps {
	row: QrTrackingRow;
	open: boolean;
	busy: boolean;
	originOverride: string;
	onToggle: () => void;
	onScan: () => void;
	onDelete: () => void;
}

function QrRow(props: QrRowProps) {
	const { row, open, busy, originOverride, onToggle, onScan, onDelete } = props;
	const shortUrl = buildShortUrlPreview(
		originOverride.trim() || DEFAULT_ORIGIN_PLACEHOLDER,
		row.slug,
	);
	const svgUrl = buildQrSvgUrl(row.slug);
	return (
		<>
			<tr>
				<td className="max-w-[160px] truncate px-4 py-2 text-zinc-900">
					{row.label || <span className="text-zinc-400">Untitled</span>}
				</td>
				<td className="px-4 py-2 font-mono text-xs text-zinc-700">{row.slug}</td>
				<td className="max-w-[220px] truncate px-4 py-2 text-zinc-700">
					<span className="font-mono text-xs" title={shortUrl}>
						{shortUrl}
					</span>
				</td>
				<td className="max-w-[220px] truncate px-4 py-2 text-zinc-700">
					<span title={row.target_url}>{row.target_url}</span>
				</td>
				<td className="px-4 py-2 tabular-nums text-zinc-900">
					{formatInteger(row.scan_count)}
				</td>
				<td className="px-4 py-2 text-zinc-700">
					{formatScanTimestamp(row.last_scan_at)}
				</td>
				<td className="px-4 py-2">
					<span
						className={
							row.status === "active" ? BADGE_ACTIVE : BADGE_DISABLED
						}
					>
						{formatStatusLabel(row.status)}
					</span>
				</td>
				<td className="px-4 py-2 text-right">
					<div className="flex justify-end gap-2">
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={onToggle}
							aria-expanded={open}
							aria-controls={`qr-preview-${row.slug}`}
						>
							{open ? "Hide" : "Preview"}
						</button>
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={onScan}
							disabled={busy || row.status !== "active"}
						>
							Record scan
						</button>
						<button
							type="button"
							className={BTN_DANGER}
							onClick={onDelete}
							disabled={busy}
						>
							Delete
						</button>
					</div>
				</td>
			</tr>
			{open ? (
				<tr id={`qr-preview-${row.slug}`}>
					<td colSpan={8} className="bg-zinc-50 px-4 py-4">
						<div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4">
							<img
								alt={`QR code for ${row.slug}`}
								src={svgUrl}
								width={176}
								height={176}
								className="rounded-[6px] border border-zinc-200 bg-white p-1"
							/>
							<div className="flex flex-col gap-1 text-sm text-zinc-700">
								<span>
									<span className="font-medium text-zinc-900">Slug:</span>{" "}
									<span className="font-mono">{row.slug}</span>
								</span>
								<span className="break-all">
									<span className="font-medium text-zinc-900">Short URL:</span>{" "}
									<span className="font-mono">{shortUrl}</span>
								</span>
								<span className="break-all">
									<span className="font-medium text-zinc-900">Target:</span>{" "}
									<span className="font-mono">{row.target_url}</span>
								</span>
								<a
									href={svgUrl}
									target="_blank"
									rel="noreferrer"
									className="mt-1 inline-flex w-fit items-center rounded-[6px] border border-zinc-300 bg-white px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
								>
									Open raw SVG
								</a>
							</div>
						</div>
					</td>
				</tr>
			) : null}
		</>
	);
}

// ---------------------------------------------------------------------------
// Summary row — small stats card.
// ---------------------------------------------------------------------------

interface SummaryRowProps {
	summary: ReturnType<typeof computeQrSummary>;
}

function SummaryRow({ summary }: SummaryRowProps) {
	const items: Array<{ label: string; value: string; tone: "primary" | "neutral" }> = [
		{ label: "Total", value: formatInteger(summary.total), tone: "primary" },
		{ label: "Active", value: formatInteger(summary.active), tone: "neutral" },
		{ label: "Disabled", value: formatInteger(summary.disabled), tone: "neutral" },
		{ label: "Total scans", value: formatInteger(summary.total_scans), tone: "primary" },
		{
			label: "Last scan",
			value: formatScanTimestamp(summary.last_scan_at),
			tone: "neutral",
		},
	];
	return (
		<div className={`${CARD} grid grid-cols-2 gap-0 p-0 sm:grid-cols-5`}>
			{items.map((it, i) => (
				<div
					key={it.label}
					className={
						"flex flex-col gap-1 px-4 py-3 " +
						(i > 0
							? "border-t border-zinc-200 sm:border-l sm:border-t-0"
							: "")
					}
				>
					<span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						{it.label}
					</span>
					<span
						className={
							"text-base font-semibold " +
							(it.tone === "primary" ? "text-emerald-700" : "text-zinc-900")
						}
					>
						{it.value}
					</span>
				</div>
			))}
		</div>
	);
}

export default QrTrackingPage;
